import React from "react";
import { HERO_STAT } from "../mock";
import { useLanguage } from "../LanguageContext";
import { BadgeCheck, ArrowRight } from "lucide-react";

const BLANK = "data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==";
const HERO_SRCSET = "/hero-researcher-640.webp 640w, /hero-researcher-960.webp 960w, /hero-researcher.webp 1264w";

// content/secondaryHref permiten reutilizar el hero en landings especiales (ej. /evento).
export default function Hero({ content, secondaryHref, cornerBadge, hideStats = false, compact = false }) {
  const { t, lp } = useLanguage();
  const h = { ...t.hero, ...content };
  const external = secondaryHref?.startsWith("http");
  return (
    <section id="top" className="relative overflow-hidden bg-white pt-28 pb-16 sm:pt-36">
      {/* Mobile (<768px): imagen como fondo de la mitad superior con fade a blanco */}
      <div className="pointer-events-none absolute inset-x-0 top-0 h-[380px] md:hidden" aria-hidden="true">
        <picture>
          <source media="(max-width: 767px)" srcSet={HERO_SRCSET} sizes="100vw" />
          <img
            src={BLANK}
            alt=""
            className="h-full w-full object-cover"
            style={{ objectPosition: "50% center" }}
            fetchPriority="high"
          />
        </picture>
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(to bottom, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.25) 45%, rgba(255,255,255,0.92) 82%, #fff 100%)",
          }}
        />
      </div>
      {cornerBadge && (
        <img
          src={cornerBadge.src}
          alt={cornerBadge.alt}
          className="absolute right-4 top-20 z-10 h-14 w-auto drop-shadow-lg md:hidden"
          data-testid="hero-corner-badge-mobile"
        />
      )}
      <div className="pointer-events-none absolute -top-40 left-1/2 h-[420px] w-[720px] -translate-x-1/2 rounded-full bg-[#AECCF9]/30 blur-[130px]" />
      <div className="pointer-events-none absolute right-[-120px] top-1/3 h-[380px] w-[380px] rounded-full bg-[#316FD9]/[0.07] blur-[110px]" />
      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid md:grid-cols-2 gap-8 lg:gap-12 items-center">
          <div className="max-md:pt-40">
            <span data-testid="hero-badge-site-ctms" className="inline-flex items-center gap-2 rounded-full border border-[#AECCF9] bg-[#E2ECFD] px-3.5 py-1.5 text-xs font-semibold tracking-wide text-[#173152]">
              <span className="h-1.5 w-1.5 rounded-full bg-[#316FD9]" />
              {h.badge}
            </span>
            <h1 className={`mt-4 md:mt-6 font-display font-extrabold leading-[1.05] text-[#173152] ${compact ? "text-3xl sm:text-4xl lg:text-5xl" : "text-4xl sm:text-5xl lg:text-6xl"}`}>
              {h.title1}{compact ? " " : <br />}
              {h.title2}
              {h.highlight ? (
                <>
                  <br />
                  <span className="text-[#316FD9]">{h.highlight}</span>
                </>
              ) : null}
            </h1>
            <p className="mt-4 md:mt-6 max-w-xl text-base sm:text-lg leading-relaxed text-[#666666]">
              {h.subtitle}
            </p>
            <div className="mt-6 md:mt-8 flex flex-wrap items-center gap-4">
              <a href="#contact" data-testid="hero-primary-cta-button" className="inline-flex items-center gap-2 rounded-xl bg-[#316FD9] px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-[#316FD9]/25 transition-colors hover:bg-[#2558B5]">
                {h.primaryCta} <ArrowRight className="h-4 w-4" />
              </a>
              <a href={secondaryHref || lp("/modulos")} {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})} data-testid="hero-secondary-cta-button" className="inline-flex items-center rounded-xl border-2 border-[#316FD9] bg-white px-6 py-3 text-sm font-semibold text-[#173152] transition-colors hover:bg-[#F0F5FF]">
                {h.secondaryCta}
              </a>
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3">
              {h.badges.map((b) => (
                <span key={b} className="inline-flex items-center gap-2 text-sm text-[#666666]">
                  <BadgeCheck className="h-4 w-4 text-[#316FD9]" /> {b}
                </span>
              ))}
            </div>
          </div>

          <div className="relative hidden md:block">
            <div className="relative rounded-2xl border border-slate-200 bg-white p-2 shadow-2xl shadow-[#173152]/10 max-w-lg mx-auto lg:max-w-none">
              <picture>
                <source media="(min-width: 768px)" srcSet={HERO_SRCSET} sizes="50vw" />
                <img
                  src={BLANK}
                  alt="Clinical researcher reviewing dashboards"
                  className="w-full h-[320px] md:h-[350px] lg:h-[390px] object-cover rounded-xl"
                  fetchPriority="high"
                />
              </picture>
              {cornerBadge && (
                <img
                  src={cornerBadge.src}
                  alt={cornerBadge.alt}
                  className={`absolute right-5 z-10 h-16 w-auto drop-shadow-lg lg:h-20 ${hideStats ? "top-5" : "top-10"}`}
                  data-testid="hero-corner-badge"
                />
              )}
              {!hideStats && (<>
              <div className="absolute -top-4 right-6 bm-float rounded-lg border border-slate-200 bg-white/95 px-3.5 py-2 shadow-xl backdrop-blur">
                <span className="flex items-center gap-2 text-xs font-medium text-[#173152]">
                  <span className="h-2 w-2 rounded-full bg-emerald-500" /> {h.floatTag}
                </span>
              </div>
              <div className="absolute -bottom-5 left-5 rounded-xl border border-slate-100 bg-white px-5 py-3 shadow-xl">
                <div className="flex items-center gap-3">
                  <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-br from-[#316FD9] to-[#173152]">
                    <svg viewBox="0 0 24 24" className="h-4 w-4 text-white" fill="currentColor"><circle cx="12" cy="5" r="1.8"/><circle cx="5" cy="17" r="1.8"/><circle cx="19" cy="17" r="1.8"/></svg>
                  </span>
                  <div>
                    <div className="font-display text-xl font-bold text-[#173152] leading-none">{HERO_STAT}</div>
                    <div className="text-xs text-[#666666] mt-0.5">{h.statLabel}</div>
                  </div>
                </div>
              </div>
              </>)}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
